import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LuSearch } from "react-icons/lu";

import { getReviewSessionPatients } from "../../../../services/case-manager/readingReviewSession";
import { useOnClickOutside } from "../../../../hooks/useClickOutside";
import arrowRightIcon from "../../../../assets/icons/reading-review/arrow-right.svg";
import sortIcon from "../../../../assets/icons/reading-review/sort.svg";
import { formatDob, fullName } from "./format";
import styles from "./ReadingReviewSession.module.css";

// "Reading Thresholds" options -> API #8 `sort`.
export const SORT_OPTIONS = [
  { value: "oor_desc", label: "OOR High - Low" },
  { value: "oor_asc", label: "OOR Low - High" },
  { value: "readings_desc", label: "Readings High - Low" },
  { value: "readings_asc", label: "Readings Low - High" },
  { value: "name", label: "Sort by Name" },
];

// SCRUM-852: the Session List - exactly the patients locked in the session
// (API #8), searchable by name/id and re-ordered by the Reading Thresholds
// menu (server-side). Clicking a row moves the workspace to that patient.
const SessionListPanel = ({ accId, sessionId, currentPatientId, refreshKey, onSelectPatient, onClose }) => {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sort, setSort] = useState("position");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useOnClickOutside(() => setMenuOpen(false));

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isError } = useQuery(
    ["reading-review", "session-patients", sessionId, debounced, sort, refreshKey],
    () => getReviewSessionPatients(accId, sessionId, { search: debounced, sort }),
    { enabled: !!sessionId, keepPreviousData: true, staleTime: 0 }
  );
  const patients = data?.patients || [];

  return (
    <aside className={styles.listPanel} aria-label="Session List">
      <div className={styles.listHeader}>
        <button type="button" className={styles.listCloseBtn} onClick={onClose} aria-label="Close Session List">
          <img src={arrowRightIcon} alt="" width={11.36} height={24.73} />
        </button>
        Session List
      </div>

      <div className={styles.listSearchRow} ref={menuRef}>
        <div style={{ position: "relative", flex: 1, display: "flex" }}>
          <input
            type="search"
            className={styles.listSearch}
            placeholder="Search Patient"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search patients in this session"
          />
          <LuSearch
            size={18}
            style={{ position: "absolute", right: 9, top: 9, color: "#0d1139", pointerEvents: "none" }}
          />
        </div>
        <button
          type="button"
          className={styles.sortBtn}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Reading Thresholds"
          aria-expanded={menuOpen}
          title="Reading Thresholds"
        >
          <img src={sortIcon} alt="" width={24} height={24} />
        </button>
        {menuOpen && (
          <div className={styles.sortMenu} role="menu">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={sort === opt.value}
                className={`${styles.sortOption} ${sort === opt.value ? styles.sortOptionActive : ""}`}
                onClick={() => {
                  // Picking the active option again goes back to session order.
                  setSort(sort === opt.value ? "position" : opt.value);
                  setMenuOpen(false);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={styles.listBody}>
        {isLoading ? (
          <div className={styles.emptyState}>Loading patients…</div>
        ) : isError ? (
          <div className={styles.emptyState}>Could not load the session list.</div>
        ) : patients.length === 0 ? (
          <div className={styles.emptyState}>No patients match.</div>
        ) : (
          patients.map((p) => {
            const isCurrent = String(p.patient.id) === String(currentPatientId);
            return (
              <button
                key={p.patient.id}
                type="button"
                className={`${styles.listRow} ${isCurrent ? styles.listRowCurrent : ""}`}
                onClick={() => !isCurrent && onSelectPatient(p.patient.id)}
                aria-current={isCurrent ? "true" : undefined}
              >
                <span>
                  {fullName(p.patient)}
                  <br />
                  <span className={styles.listRowMeta}>{formatDob(p.patient.dob)}</span>
                </span>
                {p.status === "completed" ? (
                  <span className={`${styles.listRowRight} ${styles.listRowReviewed}`}>Reviewed</span>
                ) : (
                  <span className={styles.listRowRight}>
                    <span className={styles.listRowMeta}>{p.reading_count} Readings</span>
                    <br />
                    <span className={styles.listRowOor}>{p.oor_count} OOR</span>
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
};

export default SessionListPanel;
